import { useState, useEffect, useCallback, useRef } from 'react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';

/**
 * useFMCompliance - fetches compliance data with smart filtering.
 *
 * @param {string} gymId - selected gym ID
 * @param {string} filter - "due_today" | "overdue" | "upcoming" | "all"
 * @param {string|null} trainerId - optional trainer filter
 * @param {string|null} search - optional member name search
 *
 * Returns { groups, overdueCount, summary, loading, error, refetch, completeMilestone, reassignMember, trainers }
 */
export function useFMCompliance(gymId, filter = 'due_today', trainerId = null, search = null) {
  const [groups, setGroups] = useState([]);
  const [overdueCount, setOverdueCount] = useState(0);
  const [summary, setSummary] = useState({ total_due: 0, total_overdue: 0, compliance_rate: 0 });
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const fetchCompliance = useCallback(async () => {
    if (!gymId) {
      setGroups([]);
      setLoading(false);
      return;
    }

    // Abort previous request
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      setLoading(true);
      setError(null);

      const params = { gym_id: gymId, filter };
      if (trainerId) params.trainer_id = trainerId;
      if (search) params.search = search;

      const [complianceRes, trainersRes] = await Promise.allSettled([
        axiosInstance.get('/fm/compliance', { params, signal: controller.signal }),
        axiosInstance.get('/fm/trainers', { params: { gym_id: gymId }, signal: controller.signal }),
      ]);

      if (complianceRes.status === 'fulfilled') {
        const data = complianceRes.value.data;
        setGroups(data.groups || []);
        setOverdueCount(data.overdue_count || 0);
        setSummary(data.summary || { total_due: 0, total_overdue: 0, compliance_rate: 0 });
      } else {
        throw complianceRes.reason;
      }

      if (trainersRes.status === 'fulfilled') {
        setTrainers(trainersRes.value.data.trainers || []);
      }
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      console.error('Failed to fetch compliance:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  }, [gymId, filter, trainerId, search]);

  useEffect(() => {
    fetchCompliance();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchCompliance]);

  /**
   * completeMilestone - marks a milestone as complete with optimistic update.
   */
  const completeMilestone = useCallback(async (memberId, milestoneType) => {
    // Optimistic update: mark milestone as completed locally
    setGroups((prev) =>
      prev.map((group) => ({
        ...group,
        members: group.members.map((m) => {
          if (m.member_id !== memberId) return m;
          return {
            ...m,
            milestones: m.milestones.map((ms) =>
              ms.type === milestoneType
                ? { ...ms, status: 'completed', overdue_days: 0 }
                : ms
            ),
          };
        }),
      }))
    );

    try {
      await axiosInstance.post(`/fm/compliance/${memberId}/complete`, {
        milestone_type: milestoneType,
      });
      toast.success('Milestone marked complete');
      // Refetch in background for accurate data
      fetchCompliance();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete milestone');
      // Revert optimistic update
      fetchCompliance();
    }
  }, [fetchCompliance]);

  /**
   * reassignMember - reassigns a member to a different trainer.
   */
  const reassignMember = useCallback(async (memberId, newTrainerId, reason) => {
    try {
      await axiosInstance.post(`/fm/members/${memberId}/reassign`, {
        trainer_id: newTrainerId,
        reason,
      });
      toast.success('Member reassigned');
      fetchCompliance();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reassign member');
      throw err;
    }
  }, [fetchCompliance]);

  return {
    groups,
    overdueCount,
    summary,
    trainers,
    loading,
    error,
    refetch: fetchCompliance,
    completeMilestone,
    reassignMember,
  };
}
